import { useMemo } from 'react'
import {
  useHuddleDateFetch,
  type HuddleDateFetcher,
} from './useHuddleDateFetch'
import {
  getHuddleYesterdayScheduleAdjustments,
  type HuddleYesterdayScheduleAdjustmentsResult,
  getHuddleYesterdayScheduleAdjustmentsOverview,
} from '@services/api/dashboard/huddle.api'
import {
  mapHuddleScheduleAdjustmentsSummaryToMetrics,
  mapHuddleScheduleEventsToTableRows,
  type HuddleScheduleEventTableRow,
} from '@utils/dashboardHuddle.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'

export type UseHuddleYesterdayScheduleAdjustmentsOptions = {
  date: string
  enabled?: boolean
  queued?: boolean
}

export type UseHuddleYesterdayScheduleAdjustmentsResult = {
  data: HuddleYesterdayScheduleAdjustmentsResult | null
  loading: boolean
  error: string | null
  summaryMetrics: DashboardMetricItemData[]
  eventRows: HuddleScheduleEventTableRow[]
}

function useHuddleYesterdayScheduleAdjustmentsFromFetcher({
  date,
  enabled = true,
  queued = false,
  fetcher,
}: UseHuddleYesterdayScheduleAdjustmentsOptions & {
  fetcher: HuddleDateFetcher<HuddleYesterdayScheduleAdjustmentsResult>
}): UseHuddleYesterdayScheduleAdjustmentsResult {
  const { data, loading, error } = useHuddleDateFetch(date, fetcher, {
    enabled,
    queued,
    errorMessage: 'Failed to load schedule adjustments',
  })

  const summaryMetrics = useMemo(
    () => mapHuddleScheduleAdjustmentsSummaryToMetrics(data?.summary),
    [data?.summary],
  )

  const eventRows = useMemo(
    () => mapHuddleScheduleEventsToTableRows(data?.events),
    [data?.events],
  )

  return { data, loading, error, summaryMetrics, eventRows }
}

export function useHuddleYesterdayScheduleAdjustments(
  options: UseHuddleYesterdayScheduleAdjustmentsOptions,
): UseHuddleYesterdayScheduleAdjustmentsResult {
  return useHuddleYesterdayScheduleAdjustmentsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayScheduleAdjustments,
  })
}
export function useHuddleYesterdayScheduleAdjustmentsOverview(
  options: UseHuddleYesterdayScheduleAdjustmentsOptions,
): UseHuddleYesterdayScheduleAdjustmentsResult {
  return useHuddleYesterdayScheduleAdjustmentsFromFetcher({
    ...options,
    fetcher: getHuddleYesterdayScheduleAdjustmentsOverview,
  })
}
